import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import './ForumCategory.css';
import './PageShell.css';

const CATEGORY_TEXT = {
  zh: {
    loading: '加载中...',
    forum: '论坛',
    create: '+ 发新帖',
    title: '标题',
    author: '作者',
    replies: '回复',
    views: '浏览',
    activity: '最后活动',
    empty: '还没有帖子，来发第一帖吧！',
    pin: '置顶',
    previous: '上一页',
    next: '下一页',
    page: (page, total) => `第 ${page} 页 / 共 ${total} 页`,
    justNow: '刚刚',
    minutesAgo: n => `${n}分钟前`,
    hoursAgo: n => `${n}小时前`,
    daysAgo: n => `${n}天前`,
  },
  en: {
    loading: 'Loading...',
    forum: 'Forum',
    create: '+ New post',
    title: 'Title',
    author: 'Author',
    replies: 'Replies',
    views: 'Views',
    activity: 'Last activity',
    empty: 'No posts yet. Start the first discussion.',
    pin: 'Pinned',
    previous: 'Previous',
    next: 'Next',
    page: (page, total) => `Page ${page} of ${total}`,
    justNow: 'Just now',
    minutesAgo: n => `${n}m ago`,
    hoursAgo: n => `${n}h ago`,
    daysAgo: n => `${n}d ago`,
  },
};

const CATEGORY_COPY = {
  en: {
    '经验分享': 'Experience sharing',
    '分享工作经验、接单技巧': 'Work experience, delivery tips, and client communication.',
  },
};

function timeAgo(dateStr, text) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60) return text.justNow;
  if (diff < 3600) return text.minutesAgo(Math.floor(diff / 60));
  if (diff < 86400) return text.hoursAgo(Math.floor(diff / 3600));
  return text.daysAgo(Math.floor(diff / 86400));
}

export default function ForumCategory() {
  const { id } = useParams();
  const { user } = useAuth();
  const { language } = useLanguage();
  const text = CATEGORY_TEXT[language] || CATEGORY_TEXT.en;
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/forum/categories/${id}/posts`, { params: { page, limit: 20 } })
      .then(res => setData(res.data))
      .finally(() => setLoading(false));
  }, [id, page]);

  const localize = value => (language === 'en' ? CATEGORY_COPY.en[value] || value : value);

  if (loading) return <div className="loading">{text.loading}</div>;
  if (!data) return null;

  const { category, posts, total } = data;

  return (
    <div className="page-shell-page forum-category-page">
      <div className="container" style={{ paddingTop: 32, paddingBottom: 48 }}>
      <div className="breadcrumb">
        <Link to="/forum">{text.forum}</Link>
        <span>/</span>
        <span>{category.icon} {localize(category.name)}</span>
      </div>

      <div className="cat-page-header">
        <div>
          <h1>{category.icon} {localize(category.name)}</h1>
          <p>{localize(category.description)}</p>
        </div>
        {user && (
          <Link to={`/forum/post/create?category=${id}`} className="btn btn-primary">
            {text.create}
          </Link>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="post-list-header">
          <span className="col-title">{text.title}</span>
          <span className="col-author">{text.author}</span>
          <span className="col-num">{text.replies}</span>
          <span className="col-num">{text.views}</span>
          <span className="col-time">{text.activity}</span>
        </div>

        {posts.length === 0 ? (
          <div style={{ padding: '48px 24px', textAlign: 'center', color: '#9ca3af' }}>
            <p>{text.empty}</p>
            {user && (
              <Link to={`/forum/post/create?category=${id}`} className="btn btn-primary" style={{ marginTop: 12 }}>{text.create}</Link>
            )}
          </div>
        ) : (
          posts.map(post => (
            <Link key={post.id} to={`/forum/posts/${post.id}`} className="post-row">
              <div className="col-title">
                {post.is_pinned === 1 && <span className="pin-badge">{text.pin}</span>}
                <span className="post-title-text">{post.title}</span>
              </div>
              <span className="col-author">{post.author_name}</span>
              <span className="col-num">{post.reply_count}</span>
              <span className="col-num">{post.view_count}</span>
              <span className="col-time">{timeAgo(post.updated_at, text)}</span>
            </Link>
          ))
        )}
      </div>

      {total > 20 && (
        <div className="pagination">
          <button disabled={page === 1} className="btn btn-outline btn-sm" onClick={() => setPage(p => p - 1)}>{text.previous}</button>
          <span>{text.page(page, Math.ceil(total / 20))}</span>
          <button disabled={page * 20 >= total} className="btn btn-outline btn-sm" onClick={() => setPage(p => p + 1)}>{text.next}</button>
        </div>
      )}
      </div>
    </div>
  );
}
